import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BRAND } from "@/lib/brand";
import { TID } from "@/constants/testIds/dv";

const NAV = [
  { id: "services", key: "nav.services", testId: TID.navServices },
  { id: "about", key: "nav.about", testId: TID.navAbout },
  { id: "projects", key: "nav.projects", testId: TID.navProjects },
  { id: "testimonials", key: "nav.testimonials", testId: "nav-link-testimonials" },
  { id: "area", key: "nav.area", testId: "nav-link-area" },
  { id: "contact", key: "nav.contact", testId: TID.navContact },
];

export const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const onAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (onAdmin) {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-black/95 backdrop-blur border-b border-white/10" : "bg-black"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" data-testid={TID.navLogo} className="flex items-center gap-3">
          <img src={BRAND.logo} alt="DV Solutions Inc." className="h-12 w-auto" />
          <span className="hidden md:block font-display uppercase tracking-tight text-white text-xl leading-none">
            DV Solutions
            <span className="block text-[10px] tracking-[0.3em] text-[#E63946] font-semibold">
              Remodeling & Painting
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <a
              key={n.id}
              href={`#${n.id}`}
              data-testid={n.testId}
              onClick={go(n.id)}
              className="text-white/80 hover:text-white text-sm uppercase tracking-[0.18em] font-semibold transition-colors relative group"
            >
              {t(n.key)}
              <span className="absolute left-0 -bottom-2 h-[2px] w-0 bg-[#E63946] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            className="hidden md:flex items-center rounded-sm border border-white/20 overflow-hidden text-[11px] font-semibold"
            data-testid={TID.langToggle}
          >
            <button
              data-testid={TID.langEn}
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 uppercase tracking-widest transition-colors ${
                lang === "en" ? "bg-[#E63946] text-white" : "text-white/70 hover:text-white"
              }`}
            >
              EN
            </button>
            <button
              data-testid={TID.langEs}
              onClick={() => setLang("es")}
              className={`px-3 py-1.5 uppercase tracking-widest transition-colors ${
                lang === "es" ? "bg-[#E63946] text-white" : "text-white/70 hover:text-white"
              }`}
            >
              ES
            </button>
          </div>

          <a
            href={BRAND.phoneHref}
            className="hidden xl:inline-flex items-center gap-2 text-white/80 hover:text-white text-sm"
          >
            <Phone className="h-4 w-4 text-[#E63946]" />
            {BRAND.phone}
          </a>

          <a
            href="#contact"
            onClick={go("contact")}
            data-testid={TID.navQuoteCta}
            className="hidden sm:inline-flex btn-primary"
          >
            {t("nav.quote")}
          </a>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid={TID.mobileMenuToggle}
            className="lg:hidden text-white p-2 -mr-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-black border-t border-white/10">
          <div className="container-x py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                onClick={go(n.id)}
                className="text-white/90 py-2 uppercase tracking-widest text-sm font-semibold border-b border-white/5"
              >
                {t(n.key)}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={() => setLang("en")}
                className={`flex-1 px-3 py-2 border border-white/20 text-sm uppercase tracking-widest ${
                  lang === "en" ? "bg-[#E63946] text-white border-[#E63946]" : "text-white/70"
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLang("es")}
                className={`flex-1 px-3 py-2 border border-white/20 text-sm uppercase tracking-widest ${
                  lang === "es" ? "bg-[#E63946] text-white border-[#E63946]" : "text-white/70"
                }`}
              >
                ES
              </button>
            </div>
            <a href="#contact" onClick={go("contact")} className="btn-primary mt-2">
              {t("nav.quote")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
